function normalizeText(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function tokenize(input: string): string[] {
  return normalizeText(input)
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

function damerauLevenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;

  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));

  for (let i = 0; i <= m; i += 1) dp[i][0] = i;
  for (let j = 0; j <= n; j += 1) dp[0][j] = j;

  for (let i = 1; i <= m; i += 1) {
    for (let j = 1; j <= n; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;

      let value = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost,
      );

      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        value = Math.min(value, dp[i - 2][j - 2] + 1);
      }

      dp[i][j] = value;
    }
  }

  return dp[m][n];
}

function typoTolerance(queryToken: string): number {
  if (queryToken.length <= 4) return 1;
  if (queryToken.length <= 8) return 2;
  return 3;
}

function fuzzyTokenMatch(nameToken: string, queryToken: string): boolean {
  if (nameToken.includes(queryToken)) return true;
  if (queryToken.length <= 2) return nameToken.startsWith(queryToken);
  if (queryToken.length <= 3) return false;

  const tolerance = typoTolerance(queryToken);

  if (Math.abs(nameToken.length - queryToken.length) <= tolerance && damerauLevenshtein(nameToken, queryToken) <= tolerance) {
    return true;
  }

  if (nameToken.length >= queryToken.length) {
    for (let i = 0; i <= nameToken.length - queryToken.length; i += 1) {
      const window = nameToken.slice(i, i + queryToken.length);
      if (damerauLevenshtein(window, queryToken) <= tolerance) {
        return true;
      }
    }
  }

  return false;
}

/** Case-insensitive fuzzy match of a query against a player's name (token + typo tolerant). */
export function matchesQuery(name: string, query: string): boolean {
  const queryTokens = tokenize(query);
  if (queryTokens.length === 0) return true;

  const nameTokens = tokenize(name);
  if (nameTokens.length === 0) return false;

  return queryTokens.every((queryToken) => nameTokens.some((nameToken) => fuzzyTokenMatch(nameToken, queryToken)));
}
